import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { callEdgeFunction } from './supabase.client';

const ENVS = ['dev', 'staging', 'production'] as const;
type EnvSchema = (typeof ENVS)[number];

const PROD_CONFIRM = 'PROD 데이터 적용';

interface ProtectedRow {
  table_name: string;
  column_name: string;
  note?: string | null;
}

interface Props {
  onClose: () => void;
}

export function OverridePanel({ onClose }: Props) {
  const [from, setFrom] = useState<EnvSchema>('dev');
  const [to, setTo] = useState<EnvSchema>('staging');
  const [confirmText, setConfirmText] = useState('');
  const [status, setStatus] = useState<{ tone: 'idle' | 'ok' | 'err'; text: string }>({
    tone: 'idle',
    text: 'Idle',
  });
  const [busy, setBusy] = useState(false);

  // Protected-column registry (override는 이 컬럼들을 대상 env 값 그대로 보존).
  const [protectedRows, setProtectedRows] = useState<ProtectedRow[]>([]);
  const [protErr, setProtErr] = useState<string | null>(null);
  const [pTable, setPTable] = useState('');
  const [pColumn, setPColumn] = useState('');
  const [pNote, setPNote] = useState('');

  const loadProtected = useCallback(async () => {
    try {
      const r = await callEdgeFunction<{ rows: ProtectedRow[] }>('override-protected', {
        op: 'list',
      });
      setProtectedRows(r.rows ?? []);
      setProtErr(null);
    } catch (e) {
      setProtErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadProtected();
  }, [loadProtected]);

  async function addProtected() {
    if (!pTable.trim() || !pColumn.trim()) {
      setProtErr('table / column 입력');
      return;
    }
    try {
      await callEdgeFunction('override-protected', {
        op: 'add',
        table_name: pTable.trim(),
        column_name: pColumn.trim(),
        note: pNote.trim() || undefined,
      });
      setPTable('');
      setPColumn('');
      setPNote('');
      await loadProtected();
    } catch (e) {
      setProtErr((e as Error).message);
    }
  }

  async function removeProtected(row: ProtectedRow) {
    try {
      await callEdgeFunction('override-protected', {
        op: 'remove',
        table_name: row.table_name,
        column_name: row.column_name,
      });
      await loadProtected();
    } catch (e) {
      setProtErr((e as Error).message);
    }
  }

  async function run() {
    if (from === to) {
      setStatus({ tone: 'err', text: 'from === to — 다른 env를 선택하세요.' });
      return;
    }
    if (to === 'production' && confirmText !== PROD_CONFIRM) {
      setStatus({ tone: 'err', text: 'PROD 컨펌 텍스트 불일치.' });
      return;
    }
    setBusy(true);
    setStatus({ tone: 'idle', text: 'Override 진행 중…' });
    try {
      const body: Record<string, unknown> = { from, to };
      if (to === 'production') body.confirmation = confirmText;
      const r = await callEdgeFunction<{ ok: boolean; tables: number; skipped?: string[] }>(
        'override',
        body
      );
      const skippedNote =
        r.skipped && r.skipped.length > 0
          ? ` — skip(양쪽 스키마에 없음): ${r.skipped.join(', ')}`
          : '';
      setStatus({
        tone: 'ok',
        text: `OK — ${r.tables} tables copied ${from} → ${to} (보호 컬럼 ${protectedRows.length}개 보존)${skippedNote}.`,
      });
    } catch (e) {
      setStatus({ tone: 'err', text: `Error: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Override (env 간 데이터 복제)</DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-3">
          <Label className="flex flex-col gap-1 text-xs">
            From
            <Select value={from} onValueChange={(v) => setFrom(v as EnvSchema)}>
              <SelectTrigger size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENVS.map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Label>
          <Label className="flex flex-col gap-1 text-xs">
            To
            <Select value={to} onValueChange={(v) => setTo(v as EnvSchema)}>
              <SelectTrigger size="sm" className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENVS.map((e) => (
                  <SelectItem key={e} value={e}>
                    {e}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Label>
        </div>

        {/* 보호(무시) 컬럼 — override 시 대상 env 값을 그대로 유지 */}
        <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-3">
          <span className="text-xs font-medium text-foreground">
            보호 컬럼 (override 시 무시 — 대상 값 유지)
          </span>
          {protErr && <span className="text-xs text-rose-600 dark:text-rose-400">{protErr}</span>}
          <ul className="max-h-40 overflow-auto text-xs">
            {protectedRows.length === 0 && (
              <li className="text-muted-foreground">등록된 보호 컬럼 없음</li>
            )}
            {protectedRows.map((r) => (
              <li
                key={`${r.table_name}.${r.column_name}`}
                className="flex items-center justify-between gap-2 border-b border-border py-1"
              >
                <span>
                  <code className="font-mono">
                    {r.table_name}.{r.column_name}
                  </code>
                  {r.note ? <span className="text-muted-foreground"> — {r.note}</span> : null}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => removeProtected(r)}
                  disabled={busy}
                >
                  삭제
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-end gap-2">
            <Input
              className="h-8 w-28 text-xs"
              placeholder="table"
              value={pTable}
              onChange={(e) => setPTable(e.target.value)}
            />
            <Input
              className="h-8 w-32 text-xs"
              placeholder="column"
              value={pColumn}
              onChange={(e) => setPColumn(e.target.value)}
            />
            <Input
              className="h-8 w-32 text-xs"
              placeholder="note (선택)"
              value={pNote}
              onChange={(e) => setPNote(e.target.value)}
            />
            <Button type="button" size="sm" onClick={addProtected} disabled={busy}>
              추가
            </Button>
          </div>
        </div>

        {to === 'production' && (
          <div className="flex flex-col gap-2 rounded-md border border-rose-300/60 bg-rose-50 p-3 text-sm dark:border-rose-900/60 dark:bg-rose-950/30">
            <span className="font-medium text-rose-700 dark:text-rose-300">
              ⚠ PRODUCTION 덮어쓰기. 아래 정확히 입력:
            </span>
            <code className="rounded bg-muted px-2 py-1 text-xs">{PROD_CONFIRM}</code>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="컨펌 텍스트"
            />
          </div>
        )}

        <pre
          className={`whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-2 text-xs ${
            status.tone === 'err'
              ? 'text-rose-600 dark:text-rose-400'
              : status.tone === 'ok'
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-foreground'
          }`}
        >
          {status.text}
        </pre>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            닫기
          </Button>
          <Button type="button" onClick={run} disabled={busy}>
            {busy ? '진행 중…' : 'Run'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
